<template>
  <div id="pageReporte1">
    <v-container fluid>
      <v-row>
        <v-col>
          <v-card class="elevation-2">
            <v-toolbar color="primary" dark flat class="report-toolbar">
              <v-icon class="mr-2">mdi-clock-alert-outline</v-icon>
              <v-toolbar-title>Movimientos pendientes y antigüedad</v-toolbar-title>
              <v-spacer></v-spacer>
              <report-filters :fields="['period', 'location_id', 'condition', 'type', 'responsible_id']" @change="onFilterChange"></report-filters>
              <v-btn outlined :loading="loadingExport" @click="downloadExcel">
                <v-icon left small>mdi-microsoft-excel</v-icon>
                <span class="d-none d-sm-inline">Descargar Excel</span>
              </v-btn>
              <v-btn outlined class="ml-2" :loading="loadingPdf" @click="downloadPdf(1)">
                <v-icon left small>mdi-file-pdf-box</v-icon>
                <span class="d-none d-sm-inline">Descargar PDF</span>
              </v-btn>
            </v-toolbar>

            <v-card-text class="pt-6">
              <div class="text-body-2 grey--text text--darken-1 mb-4">
                Ver qué movimientos y cesiones están parados, en qué fase, desde hace cuántos días y qué ubicación o persona es responsable del bloqueo.
              </div>

              <v-progress-linear v-if="loadingPreview" indeterminate color="primary" class="mb-6"></v-progress-linear>

              <!-- Vista previa estilo "Resumen" del Excel -->
              <div v-else>
                <v-alert v-if="!previewItems.length" type="info" text dense>
                  No hay datos para mostrar
                </v-alert>

                <template v-else>
                  <p class="text-body-2 grey--text text--darken-2 mb-6">
                    Situación de los movimientos y cesiones creados en el periodo. Cada estado abierto tiene un responsable:
                    <strong>No reportado</strong> → no se ha trabajado;
                    <strong>Reportado</strong> → falta la entrega (ubicación de origen);
                    <strong>En proceso</strong> → falta la recepción (ubicación de destino).
                  </p>

                  <!-- KPIs -->
                  <div class="kpi-grid mb-8">
                    <div class="kpi-card">
                      <v-icon size="30" color="blue darken-2">mdi-car-multiple</v-icon>
                      <div class="kpi-number">{{ kpis.total }}</div>
                      <div class="kpi-label">Totales</div>
                    </div>
                    <div class="kpi-card">
                      <v-icon size="30" color="red darken-1">mdi-alert-circle</v-icon>
                      <div class="kpi-number">{{ kpis.noReportado.count }}</div>
                      <div class="kpi-label">No reportados</div>
                      <div class="kpi-subtitle">máx. {{ kpis.noReportado.maxDays }} días</div>
                    </div>
                    <div class="kpi-card">
                      <v-icon size="30" color="orange darken-1">mdi-bell-ring-outline</v-icon>
                      <div class="kpi-number">{{ kpis.reportado.count }}</div>
                      <div class="kpi-label">Reportados</div>
                      <div class="kpi-subtitle">sin entregar · máx. {{ kpis.reportado.maxDays }} días</div>
                    </div>
                    <div class="kpi-card">
                      <v-icon size="30" color="purple darken-1">mdi-progress-clock</v-icon>
                      <div class="kpi-number">{{ kpis.enProceso.count }}</div>
                      <div class="kpi-label">En proceso</div>
                      <div class="kpi-subtitle">sin recepcionar · máx. {{ kpis.enProceso.maxDays }} días</div>
                    </div>
                    <div class="kpi-card">
                      <v-icon size="30" color="green darken-1">mdi-check-circle-outline</v-icon>
                      <div class="kpi-number">{{ kpis.completado.count }}</div>
                      <div class="kpi-label">Completados</div>
                      <div class="kpi-subtitle">{{ kpis.completado.pct }} % del total</div>
                    </div>
                  </div>

                  <!-- Antigüedad por estado -->
                  <div class="report-section-title">
                    <v-icon small color="primary" class="mr-1">mdi-timer-sand</v-icon>
                    Antigüedad por estado (días en el estado actual)
                  </div>
                  <v-simple-table class="report-table mb-8" dense>
                    <thead>
                      <tr>
                        <th>Estado</th>
                        <th>Qué falta</th>
                        <th>Responsable</th>
                        <th class="text-center">0–2 días</th>
                        <th class="text-center">3–7 días</th>
                        <th class="text-center">8–15 días</th>
                        <th class="text-center">&gt;15 días</th>
                        <th class="text-right">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="row in agingTable" :key="row.status">
                        <td>
                          <v-chip :color="row.color" dark x-small class="px-2">
                            <v-icon left x-small>{{ row.icon }}</v-icon>
                            {{ row.status }}
                          </v-chip>
                        </td>
                        <td>{{ row.queFalta }}</td>
                        <td>{{ row.responsable }}</td>
                        <td class="text-center">
                          <span class="bucket-pill bucket-0">{{ row.buckets[0] }}</span>
                        </td>
                        <td class="text-center">
                          <span class="bucket-pill bucket-1">{{ row.buckets[1] }}</span>
                        </td>
                        <td class="text-center">
                          <span class="bucket-pill bucket-2">{{ row.buckets[2] }}</span>
                        </td>
                        <td class="text-center">
                          <span class="bucket-pill bucket-3">{{ row.buckets[3] }}</span>
                        </td>
                        <td class="text-right font-weight-bold">{{ row.total }}</td>
                      </tr>
                    </tbody>
                  </v-simple-table>

                  <!-- Pendientes por ubicación -->
                  <div class="report-section-title">
                    <v-icon small color="primary" class="mr-1">mdi-map-marker-outline</v-icon>
                    Pendientes por ubicación
                    <span class="text-caption grey--text ml-1">(ordenado de más a menos pendientes)</span>
                  </div>
                  <v-simple-table class="report-table mb-8" dense>
                    <thead>
                      <tr>
                        <th>Ubicación</th>
                        <th class="text-center">Pend. de entregar<br><span class="text-caption grey--text">como origen</span></th>
                        <th class="text-center">Pend. de recepcionar<br><span class="text-caption grey--text">como destino</span></th>
                        <th class="text-center">Total pendientes</th>
                        <th class="text-center">Más antiguo</th>
                        <th>Peso</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="row in pendingByLocation" :key="row.location">
                        <td class="font-weight-medium">{{ row.location }}</td>
                        <td class="text-center">
                          <span v-if="row.pendEntregar >= 3" class="bucket-pill bucket-2">{{ row.pendEntregar }}</span>
                          <span v-else>{{ row.pendEntregar }}</span>
                        </td>
                        <td class="text-center">
                          <span v-if="row.pendRecepcionar >= 3" class="bucket-pill bucket-2">{{ row.pendRecepcionar }}</span>
                          <span v-else>{{ row.pendRecepcionar }}</span>
                        </td>
                        <td class="text-center font-weight-bold">{{ row.total }}</td>
                        <td class="text-center">
                          <span class="bucket-pill" :class="daysBucketClass(row.maxDays)">{{ row.maxDays }} d</span>
                        </td>
                        <td>
                          <div class="weight-bar-bg">
                            <div class="weight-bar-fill" :class="row.weightPct >= 75 ? 'weight-bar-red' : 'weight-bar-orange'" :style="{ width: row.weightPct + '%' }"></div>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </v-simple-table>

                  <!-- Detalle de pendientes -->
                  <div class="report-section-title">
                    <v-icon small color="primary" class="mr-1">mdi-format-list-bulleted</v-icon>
                    Detalle de pendientes
                  </div>
                  <v-data-table
                    :headers="pendingDetailHeaders"
                    :items="pendingDetail"
                    class="report-table report-table--link"
                    @click:row="row => openRecord(row)"
                    dense
                    item-key="id"
                    :footer-props="{ itemsPerPageOptions: [10, 25, 50] }"
                    :items-per-page="10"
                  >
                    <template v-slot:item.vehicle="{ item }">
                      <div class="font-weight-medium">{{ item.vehicle }}</div>
                      <div class="text-caption grey--text">Matrícula: {{ item.plate || '—' }} <template v-if="item.iic"> · IIC {{ item.iic }}</template></div>
                      <div class="text-caption grey--text">Bastidor: {{ item.frame || '—' }}</div>
                    </template>
                    <template v-slot:item.route="{ item }">
                      {{ item.origin }} <v-icon x-small>mdi-arrow-right</v-icon> {{ item.destination }}
                    </template>
                    <template v-slot:item.status="{ item }">
                      <v-chip :color="getStatusColor(item.status)" dark x-small>{{ item.status }}</v-chip>
                    </template>
                    <template v-slot:item.days_in_state="{ item }">
                      <span class="bucket-pill" :class="daysBucketClass(item.days_in_state)">{{ item.days_in_state }}</span>
                    </template>
                    <template v-slot:item.created_at="{ item }">
                      {{ formatDateOnly(item.created_at) }}
                    </template>
                  </v-data-table>
                </template>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import moment from 'moment'
import ReportService from '../../services/ReportService'
import errorHandler from '../../mixins/errorHandler'
import reportPdf from '../../mixins/reportPdf'
import reportNavigation from '../../mixins/reportNavigation'
import ReportFilters from '../../components/ReportFilters'

const STATUS_META = {
  'No reportado': { color: 'red darken-1', icon: 'mdi-alert-circle', queFalta: 'Trabajar el movimiento', responsable: 'Equipo que gestiona los movimientos' },
  'Reportado': { color: 'orange darken-1', icon: 'mdi-bell-ring-outline', queFalta: 'Entrega', responsable: 'Ubicación de origen' },
  'En proceso': { color: 'purple darken-1', icon: 'mdi-progress-clock', queFalta: 'Recepción', responsable: 'Ubicación de destino' },
  'Completado': { color: 'green darken-1', icon: 'mdi-check-circle-outline', queFalta: '', responsable: '' }
}

export default {
  name: 'Reporte1',

  components: { ReportFilters },

  mixins: [errorHandler, reportPdf, reportNavigation],

  data: () => ({
    params: { period: 'this_month' },

    previewItems: [],
    loadingPreview: true,
    loadingExport: false,

    pendingDetailHeaders: [
      { text: 'Tipo', value: 'type' },
      { text: 'Vehículo', value: 'vehicle' },
      { text: 'Ubicaciones', value: 'route', sortable: false },
      { text: 'Estado', value: 'status' },
      { text: 'Días en estado', value: 'days_in_state' },
      { text: 'Creación', value: 'created_at' },
      { text: 'Responsable', value: 'responsible' }
    ]
  }),

  computed: {
    ...mapGetters(['HAS_PERMISSION']),

    kpis () {
      const total = this.previewItems.length
      const byStatus = (status) => this.previewItems.filter(row => row.status === status)
      const maxDays = (rows) => rows.reduce((max, row) => Math.max(max, row.days_in_state || 0), 0)

      const noReportado = byStatus('No reportado')
      const reportado = byStatus('Reportado')
      const enProceso = byStatus('En proceso')
      const completado = byStatus('Completado')

      return {
        total,
        noReportado: { count: noReportado.length, maxDays: maxDays(noReportado) },
        reportado: { count: reportado.length, maxDays: maxDays(reportado) },
        enProceso: { count: enProceso.length, maxDays: maxDays(enProceso) },
        completado: { count: completado.length, pct: total ? Math.round((completado.length / total) * 100) : 0 }
      }
    },

    agingTable () {
      const states = ['No reportado', 'Reportado', 'En proceso']
      return states.map(status => {
        const meta = STATUS_META[status]
        const rows = this.previewItems.filter(row => row.status === status)
        const buckets = [0, 0, 0, 0]
        rows.forEach(row => {
          const days = row.days_in_state || 0
          if (days <= 2) buckets[0]++
          else if (days <= 7) buckets[1]++
          else if (days <= 15) buckets[2]++
          else buckets[3]++
        })
        return {
          status,
          color: meta.color,
          icon: meta.icon,
          queFalta: meta.queFalta,
          responsable: meta.responsable,
          buckets,
          total: rows.length
        }
      })
    },

    pendingByLocation () {
      const map = {}

      const addToLocation = (location, field, days) => {
        if (!location) return
        if (!map[location]) {
          map[location] = { location, pendEntregar: 0, pendRecepcionar: 0, maxDays: 0 }
        }
        map[location][field]++
        map[location].maxDays = Math.max(map[location].maxDays, days || 0)
      }

      this.previewItems.forEach(row => {
        if (row.status === 'Reportado') addToLocation(row.origin, 'pendEntregar', row.days_in_state)
        if (row.status === 'En proceso') addToLocation(row.destination, 'pendRecepcionar', row.days_in_state)
      })

      const list = Object.values(map).map(item => ({ ...item, total: item.pendEntregar + item.pendRecepcionar }))
      list.sort((a, b) => b.total - a.total)

      const maxTotal = list.length ? list[0].total : 0
      return list.map(item => ({
        ...item,
        weightPct: maxTotal ? Math.round((item.total / maxTotal) * 100) : 0
      }))
    },

    pendingDetail () {
      return this.previewItems
        .filter(row => row.status !== 'Completado')
        .slice()
        .sort((a, b) => (b.days_in_state || 0) - (a.days_in_state || 0))
    }
  },

  methods: {
    getStatusColor (status) {
      return (STATUS_META[status] && STATUS_META[status].color) || 'grey'
    },

    daysBucketClass (days) {
      const value = days || 0
      if (value <= 2) return 'bucket-0'
      if (value <= 7) return 'bucket-1'
      if (value <= 15) return 'bucket-2'
      return 'bucket-3'
    },

    formatDateOnly (value) {
      if (!value) return ''
      return moment.unix(value).format('DD/MM/YYYY')
    },

    async loadPreview () {
      try {
        this.loadingPreview = true
        // console.log('[R1] loadPreview -> params enviados:', JSON.stringify(this.params))
        const response = await ReportService.getReport1(this.params)
        const data = response.data
        // console.log('[R1] request:', response.config.method, response.config.url, JSON.stringify(response.config.params))
        // console.log('[R1] response status:', response.status, 'filas:', (data.data || []).length, data)
        this.previewItems = data.data || []
      } catch (error) {
        // console.error('[R1] loadPreview error:', error, error.response && error.response.data)
        this.handleApiError(error, 'Error al cargar los datos del reporte')
      } finally {
        this.loadingPreview = false
      }
    },

    async downloadExcel () {
      try {
        this.loadingExport = true
        const response = await ReportService.downloadReport1(this.params)
        const blob = response.data

        if (!blob || blob.size === 0) {
          throw new Error('El archivo descargado está vacío')
        }

        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        const timestamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')
        link.download = `reporte1_${timestamp}.xlsx`
        link.style.display = 'none'

        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)

        this.$toast.success('Reporte descargado correctamente')
      } catch (error) {
        this.handleApiError(error, 'Error al descargar el reporte')
      } finally {
        this.loadingExport = false
      }
    },

    onFilterChange (params) {
      // console.log('[R1] onFilterChange params:', JSON.stringify(params))
      this.params = params
      this.loadPreview()
    }
  },

  created () {
    this.loadPreview()
  }
}
</script>

<style scoped>
.report-table--link >>> tbody tr {
  cursor: pointer;
}

.kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 16px;
}

.kpi-card {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px 12px;
  text-align: center;
}

.kpi-number {
  font-size: 30px;
  font-weight: 600;
  line-height: 1.3;
  margin-top: 4px;
}

.kpi-label {
  font-size: 14px;
  color: rgba(0, 0, 0, 0.7);
}

.kpi-subtitle {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.5);
}

.report-section-title {
  display: flex;
  align-items: center;
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 12px;
}

.report-table {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
}

.report-table >>> thead th {
  background-color: #fafafa;
  font-weight: 600 !important;
}

.bucket-pill {
  display: inline-block;
  min-width: 28px;
  padding: 2px 8px;
  border-radius: 12px;
  font-weight: 500;
  text-align: center;
}

.bucket-0 {
  background-color: #E8F5E9;
  color: #2E7D32;
}

.bucket-1 {
  background-color: #FFF8E1;
  color: #F9A825;
}

.bucket-2 {
  background-color: #FFF3E0;
  color: #EF6C00;
}

.bucket-3 {
  background-color: #FFEBEE;
  color: #C62828;
}

.weight-bar-bg {
  width: 100%;
  min-width: 100px;
  height: 8px;
  background-color: #eeeeee;
  border-radius: 4px;
  overflow: hidden;
}

.weight-bar-fill {
  height: 100%;
  border-radius: 4px;
}

.weight-bar-red {
  background-color: #E53935;
}

.weight-bar-orange {
  background-color: #FB8C00;
}
</style>
